import { ComponentType } from "react";
import { useDispatch } from "react-redux";

import { EvaChart } from "constants/charts.enums";
import { ChartStatus, useChartsContext } from "contexts/ChartContext";
import { UNDEFINED_LOCKS } from "entities/charts/chart";
import { createFeatureEntities } from "entities/charts/eva/features";
import { updateChart } from "store/features";
import styled from "styled-components";

import { useChartEntities } from "hooks/charts";

import { BaseMenu } from "components/base";
import Icon_RigFracActivityVsTime from "components/icons/Icon_RigFracActivityVsTime";
import Icon_RigFracRecentActivity from "components/icons/Icon_RigFracRecentActivity";

import { RIG_FRAC_CHART_TYPES } from "../../../constants";
import {
  ChartTypeButton,
  ChartTypeMenuContainer
} from "../components/ChartSelector/ChartSelector.styles";
import ChartTypeHeader from "../components/ChartTypeHeader";

/**
 * Maps each Rig/Frac chart type to its selector icon. ComponentType (not a bare
 * function type) so memo-wrapped icon components are assignable too.
 */
const RIG_FRAC_CHART_ICONS: Partial<Record<EvaChart, ComponentType>> = {
  [EvaChart.RigFracActivity]: Icon_RigFracActivityVsTime,
  [EvaChart.RigFracRecentActivity]: Icon_RigFracRecentActivity
};

function RigFracChartIcon({ chartType }: { chartType: EvaChart }) {
  const Icon = RIG_FRAC_CHART_ICONS[chartType];
  return Icon ? <Icon /> : null;
}

/**
 * Scoped chart selector for the Rig/Frac Charts widget (EVA-76 US4/T029).
 * Renders the RIG_FRAC_CHART_TYPES groups only (Activity vs Time + Recent
 * Activity). A local sibling of ChartSelector: the shared one is wells-bound
 * (session restore, product defaults, positional icon arrays).
 */
function RigFracChartSelector() {
  const dispatch = useDispatch();
  const { id, setStatus } = useChartsContext();
  const { chartType } = useChartEntities(id);

  const handleSelect = (nextChartType: EvaChart, closeMenu: () => void) => {
    closeMenu();
    if (nextChartType === chartType) return;

    setStatus(ChartStatus.Loading);
    dispatch(
      updateChart({
        id,
        chartType: nextChartType,
        features: createFeatureEntities(nextChartType),
        locks: UNDEFINED_LOCKS,
        response: undefined
      })
    );
  };

  return (
    <div className="charts-selector flex">
      <BaseMenu
        trigger={
          <TriggerWrapper
            aria-label="Select Chart Type"
            data-testid="rig-frac-chart-type-selector">
            <RigFracChartIcon chartType={chartType as EvaChart} />
          </TriggerWrapper>
        }>
        {({ closeMenu }) => (
          <ChartTypeMenuContainer>
            {RIG_FRAC_CHART_TYPES.map((header) => (
              <ChartTypeHeader key={header.name} header={header.name}>
                {header.items.map((ct) => (
                  <ChartTypeButton
                    key={ct}
                    className={ct === chartType ? "isSelected" : "isAvailable"}
                    data-testid={`rig-frac-chart-type-${ct}`}
                    onClick={() => handleSelect(ct as EvaChart, closeMenu)}>
                    <RigFracChartIcon chartType={ct as EvaChart} />
                    <span>{ct}</span>
                  </ChartTypeButton>
                ))}
              </ChartTypeHeader>
            ))}
          </ChartTypeMenuContainer>
        )}
      </BaseMenu>
    </div>
  );
}

export default RigFracChartSelector;

const TriggerWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
  cursor: pointer;

  svg {
    color: #a2aaad;
    transition: color var(--duration-short);
  }

  &:hover svg {
    color: var(--color-primary);
  }
`;
